<template>
  <div>
    <popup-hint hint="fermenters">
      <template v-slot:breadcrumbs>
        <breadcrumbs home="Fermenters">
          <template v-slot:left v-if="maintenanceDueCount">
            <list-notification v-model="showStatus">
              <v-list-item>
                <v-icon>mdi-arrow-right-thin</v-icon>
                  {{maintenanceDueCount}} fermenter{{maintenanceDueCount>1?'s':''}} due for maintenance
                <v-spacer/>
                <v-btn small color="warning" @click="showStatus=false;$nextTick(() => {filters.siteId=null;filters.status='1';;filters.maintenanceDue='1';filters.asof=today})">View</v-btn>
              </v-list-item>
            </list-notification>
          </template>
        </breadcrumbs>
      </template>
      <template v-slot:top>
        Fermenters are required in order to do mashing.
      </template>
      <template v-slot:bottom>
        You can create fermenters individually or do a batch import of fermenters.
      </template>
      <template v-slot:actions="{close}">
        <v-btn color="primary" class="ml-4" small @click="close();$refs.import.doImport()">Import</v-btn>
        <v-btn @click="close" :to="{name:'EquipmentFermentersEditNew'}" color="primary" class="ml-4" small>Create</v-btn>
      </template>
    </popup-hint>
    <import-csv v-model="doImport" type="fermenters" :fields="importFields" importAction="fermentersImport" :instructions="instructions" ref="import" @refresh="refresh"/>

    <purtrak-table name="fermenters" objectType="fermenters" :defaultColumns="defaultColumns" :headers="headers" ref="table" show-select show-expand
                   listEndPoint="fermenters2" v-model="selected" @list="listBuilt"
                   fields="id,name,site{id,name},active,
                            manufacturer,modelNumber,serialNumber,maintenanceDue,capacity,
                            contents {volume,lot,value,fermentingDays,hasNotes,mashId}"
                   totalFields="totalVolume,totalValue"
                   :totals.sync="totals"
                   :listFilters="listFilters" :parameters="listParameters"
                   @dblclick="fermenter => $router.push({name:'EquipmentFermentersTransactions',params:{fermenterId:fermenter.id}})"
                   @itemExpanded="({item, value}) => expand(item, value)"
                   :item-class="fermenter => (fermenter.active ? '' : 'grey--text')"
                   title="Fermenters"
                   :print-info="{pageSize:'landscape',
                                 subTitle:[`As of ${$formatDate(filters.asof)}`,filters.maintenanceDue=='1'?'Maintenance due':'',filters.status!='1'?(filters.status=='0'?'Inactive':'All'):'',
                                            filters.siteId!=null?siteName:'']}"
                   download-endpoint="fermentersExportCSV">
      <template v-slot:filter>
        <scan-code @code="scan" return-unmatched-code auto-close/>
        <filter-menu include-reset @reset="filters.siteId=null;filters.status='1';filters.maintenanceDue='0';filters.asof=today;filters.id=null">
          <v-list-item>
            <filter-search :label="[{text:'Name',value:'name'},{text:'Lot',value:'contents.lot'}]" v-model="filters.search" :search-field.sync="filters.searchField" :search-display.sync="searchDisplay"/>
          </v-list-item>
          <v-list-item class="mt-n5">
            <v-checkbox v-model="filters.maintenanceDue" true-value="1" false-value="0" label="Maintenance due"/>
          </v-list-item>
          <v-list-item>
            <v-autocomplete label="Status" dense outlined :items="[{value:'2',text:'All'},{divider:true},{value:'1',text:'Active'},{value:'0',text:'Inactive'}]" v-model="filters.status"/>
          </v-list-item>
          <v-list-item v-if="sites.length>1">
            <v-autocomplete label="Site" dense outlined :items="[{id:null,name:'All'},{divider:true},...sites]" v-model="filters.siteId" item-text="name" item-value="id"/>
          </v-list-item>
          <v-list-item>
            <date-picker label="As of" v-model="filters.asof" date-only/>
          </v-list-item>
        </filter-menu>
        <template v-if="!$vuetify.breakpoint.mobile">
          <v-chip v-show="filters.id" close small color="primary" class="ml-1" @click:close="filters.id=null">Scanned fermenter</v-chip>
          <v-chip v-show="filters.asof!==today" close small class="ml-1" @click:close="filters.asof=today">As of {{filters.asof | formatDate}}</v-chip>
          <v-chip v-show="filters.search" close small class="ml-1" @click:close="filters.search=''">{{searchDisplay}}</v-chip>
          <v-chip v-show="filters.maintenanceDue=='1'" close small class="ml-1" @click:close="filters.maintenanceDue='0'">Maintenance due</v-chip>
          <v-chip v-show="filters.status!='1'" close small class="ml-1" @click:close="filters.status='1'">{{filters.status=='0'?'Inactive':'All'}}</v-chip>
          <v-chip v-show="filters.siteId!=null" close small class="ml-1" @click:close="filters.siteId=null">{{siteName}}</v-chip>
        </template>
      </template>
      <template v-slot:menu>
        <template v-if="!readOnly">
          <v-list-item :to="{name:'EquipmentFermentersEditNew'}"><v-list-item-title>Create fermenter</v-list-item-title></v-list-item>
          <v-list-item @click="doImport=true" v-if="!$vuetify.breakpoint.mobile"><v-list-item-title>Import fermenters from CSV</v-list-item-title></v-list-item>
        </template>
        <template v-if="$store.getters.company.connectedToAccounting">
          <v-divider/>
          <v-list-item @click="sync"><v-list-item-title>Sync fermenters with QBO</v-list-item-title></v-list-item>
        </template>
      </template>
      <template v-slot:footer v-if="selected.length">
        <v-icon style="position: absolute" class="mt-3 ml-3">mdi-arrow-up-left</v-icon>
        <equipment-fermenters-batch-edit :fermenters="selected" @refresh="$refs.table.buildList($event)"/>
      </template>
      <template v-slot:item.name="{item,printing}">
        <template v-if="printing">{{item.name}}</template>
        <template v-else>
          <v-hover v-if="!readOnly" v-slot="{hover}">
            <a @click.stop="$router.push({name:'EquipmentFermentersEdit',params:{fermenterId:item.id}})"
              :class="{'text-decoration-underline':hover,'red--text':item.maintenanceDue,'grey--text':!item.active}">{{item.name}}</a>
          </v-hover>
          <div v-else :class="{'red--text':item.maintenanceDue,'grey--text':!item.active}">{{item.name}}</div>
          <div v-if="item.maintenanceDue" class="grey--text text-caption">
            <v-btn plain color="error" x-small :to="{name:'EquipmentMaintenance',params:{equipmentId:item.id}}" class="px-0">
              Maintenance due
            </v-btn>
          </div>
          <div v-else-if="!item.active" class="grey--text text-caption">Inactive</div>
        </template>
      </template>
      <template v-slot:item.contents.lot="{item,printing}">
        <template v-if="!item.contents.lot || printing">{{item.contents.lot}}</template>
        <v-hover v-else v-slot="{hover}">
          <a @click.stop="$router.push({name:'ReportsView',params:{back:[{text:'Fermenters',to:{name:'EquipmentFermenters'}}]},query:{to:'reports/inventory/ReportsLot',id:item.id,lot:item.contents.lot,forward:1}})"
            :class="{'text-decoration-underline':hover}">{{item.contents.lot}}</a>
        </v-hover>
      </template>
      <template v-slot:item.contents.fermentingDays="{item,printing}">
        <template v-if="item.contents.fermentingDays!=undefined">
          {{item.contents.fermentingDays}} days
        </template>
        <template v-else-if="printing">-</template>
      </template>
      <template v-slot:item.contents.volume="{value}">
        {{value | number(0)}} litres
      </template>
      <template v-slot:item.maintenanceDue="{value}">
        {{value | formatDate}}
      </template>
      <template v-slot:item.capacity="{value}">
        {{value | number(0)}} litres
      </template>
      <template v-slot:item.contents.value="{value}">
        {{value | currency}}
      </template>
      <template v-slot:header.action v-if="!readOnly">
        <v-btn fab color="primary" small :to="{name:'EquipmentFermentersEditNew'}"><v-icon>mdi-plus</v-icon></v-btn>
      </template>
      <template v-slot:header.notes>
        <v-icon>mdi-history</v-icon>
      </template>
      <template v-slot:item.notes="{item}">
        <v-btn v-if="item.contents.mashId" icon :color="item.contents.hasNotes?'green':'grey lighten-2'" small @click="fermenter=item;showNotes=true"><v-icon>mdi-history</v-icon></v-btn>
      </template>
      <template v-slot:item.action="{item}">
        <equipment-fermenter-menu :fermenter="item" :readOnly="readOnly"
                @delete="fermenter=item;showDelete=true"
                @mashNotes="fermenter=item;showNotes=true"/>
      </template>
      <template v-slot:body.append="{headers,printing}" v-if="!$vuetify.breakpoint.mobile">
        <tr :class="darkMode ? 'total-row-dark' : 'total-row'">
          <template v-for="header of headers">
            <td class="text-right" v-if="header.value==='contents.volume'">
              {{totals.totalVolume | number(0)}} litres
            </td>
            <td class="text-right" v-else-if="header.value==='contents.value' && values">
              {{totals.totalValue | currency}}
            </td>
            <td v-else-if="!printing || header.print === undefined || header.print"/>
          </template>
        </tr>
      </template>
      <template v-slot:expanded-item="{ headers,item }">
        <equipment-list-expand :width="headers.length" :equipment="item"/>
      </template>
    </purtrak-table>

    <delete-object v-model="showDelete" :name="fermenter.name" @delete="performDeleteFermenter"/>
    <production-mashing-notes :mashId="fermenter.contents.mashId" v-model="showNotes" :hasNotes.sync="fermenter.contents.hasNotes"/>
  </div>
</template>

<script>
import EquipmentFermenterMenu from '@/views/equipment/fermenters/EquipmentFermenterMenu'
import EquipmentListExpand from '@/views/equipment/EquipmentListExpand'
import {format} from 'date-fns'
import ProductionMashingNotes from '@/views/production/mashing/ProductionMashingNotes'
import EquipmentFermentersBatchEdit from '@/views/equipment/fermenters/EquipmentFermentersBatchEdit'

export default {
  name: 'EquipmentFermenters',
  components: {EquipmentFermenterMenu,EquipmentListExpand,ProductionMashingNotes,EquipmentFermentersBatchEdit},
  data() {
    return {
      maintenanceDueCount: 0,
      showStatus: false,
      filters: {
        maintenanceDue: '0',
        asof: format(new Date(), 'yyyy-MM-dd'),
        status: '1',
        siteId: null,
        searchField: 'name',
        search: '',
        id: null
      },
      totals: {totalValue:0, totalVolume:0},
      today: format(new Date(), 'yyyy-MM-dd'),
      searchDisplay: '',
      fermenter: {contents:{}},
      showNotes: false,
      showDelete: false,
      doImport: false,
      importFields: [{name:'Name',mapTo:'',mandatory:true},{name:'Capacity',mapTo:'',mandatory:false},
                     {name:'Initial volume',mapTo:'',mandatory:false},{name:'Initial value',mapTo:'',mandatory:false},
                     {name:'Initial date',mapTo:'',mandatory:false,date:true},{name:'Initial lot',mapTo:'',mandatory:false}],
      instructions: `<ol class="mb-4">
                        <li>Name</li>
                        <li>Capacity (optional, default is 1000 litres)</li>
                        <li>Initial volume (optional, default is 0 litres)</li>
                        <li>Initial value (optional, default is $0)</li>
                        <li>Initial date (optional, default is today)</li>
                        <li>Initial lot (optional, default is L0)</li>
                      </ol>
                      <em>Note that not all information for a fermenter can be imported.</em>
                      You should, therefore, check each imported fermenter to ensure all settings are correct.`,
      defaultColumns: [{ value: 'name', visible:true},
                       { value: 'notes', visible:true,excludeFromExport:true},
                       { value: 'site.name', visible:true},
                       { value: 'contents.lot', visible:true},
                       { value: 'contents.fermentingDays', visible:true},
                       { value: 'contents.volume', visible:true},
                       { value: 'contents.value', visible:true},
                       { value: 'maintenanceDue', visible:false},
                       { value: 'capacity', visible:false}],
      headers: [{ text: 'FERMENTER', value: 'name', width:'20%',sortable:true},
                { text: 'NOTES', value: 'notes', width:'5%',sortable:false,align:'center',print:false},
                { text: 'SITE', value: 'site.name', width:'19%',sortable:true,omit:() => this.sites.length <= 1},
                { text: 'LOT', value: 'contents.lot',width:'15%',sortable:true},
                { text: 'AGE', value: 'contents.fermentingDays',align:'right', width:'10%',sortable:true},
                { text: 'VOLUME', value: 'contents.volume', width:'15%',sortable:true,align:'right'},
                { text: 'MAINTENANCE', value: 'maintenanceDue', width:'15%',sortable:true},
                { text: 'CAPACITY', value: 'capacity', width:'15%',sortable:true,align:'right'},
                { text: 'VALUE', value: 'contents.value', width:'15%',sortable:true,align:'right', omit:() => !this.values}]
    }
  },
  computed: {
    listFilters() {
      const filters = [];
      if (this.filters.id)
        filters.push({field:'id', comparator:'eq', value:this.filters.id});
      if (this.filters.maintenanceDue == '1')
        filters.push({field:'maintenanceDue', comparator:'ne', value:'null'});
      if (this.filters.status == '1' || this.filters.status == '0')
        filters.push({field:'active', comparator:'eq', value:this.filters.status == '1' ? 'true' : 'false'});

      if (this.filters.siteId)
        filters.push({field:'siteId', comparator:'eq', value:this.filters.siteId});

      if (this.filters.search)
        filters.push({field:this.filters.searchField, comparator:'contains', value:this.filters.search});

      return filters;
    },
    listParameters() {
      return this.filters.asof ? [{name:'asof', value:`${this.filters.asof} 23:59:59`}] : [];
    },
    sites() {
      return this.$store.getters.sites(true, {B265:true,B266:true});
    },
    siteName() {
      return this.sites.find(site => site.id === this.filters.siteId)?.name;
    },
    values() {
      return this.$store.getters.company.permissions.includes('values');
    },
    readOnly() {
      return !this.$store.getters.company.permissions.includes('production.write');
    }
  },
  async created() {
    this.$store.dispatch('checkTasks');

    // Maintenance due?
    const results = await this.$apiCall(`query EquipmentFermenters {
                                            equipmentMaintenanceDue{typeName}
                                          }`,
                                      {});
    if (!results.error)
      this.maintenanceDueCount = results.data.equipmentMaintenanceDue.filter(e => e.typeName === 'Fermenter').length;

    if (!['EquipmentFermentersEdit', 'EquipmentFermentersEditNew', 'EquipmentFermentersTransactions'].includes(this._previousRoute) &&
        Object.keys(this.$route.query).length === 0 &&
        this.maintenanceDueCount && !localStorage.getItem('hideNotification')) {
      this.$nextTick(() => this.showStatus = true);
      setTimeout(() => this.showStatus = false, 10_000);  // Close in 10 seconds
    }
  },
  methods: {
    scan({type, id}) {
      if (type === 'Fermenter')
        Object.assign(this.filters, {siteId:null,status:'2',maintenanceDue:'0',asof:this.today,id});
    },
    listBuilt({list}) {
      if (this.filters.id && list.edges.length === 1)
        this.$refs.table.expanded.push(list.edges[0].node);
    },
    async expand(item, value) {
      if (value && !item.maintenance) {
        this.loading = true;
        const results = await this.$apiCall(`query EquipmentFermenters($id:ID!) {
                                                  fermenter(id:$id) {
                                                    maintenance {frequency,frequencyUnits,nextDate,description}
                                                  }
                                                }`,
                                            {id:item.id});
        this.$set(item, 'maintenance', results.data.fermenter.maintenance);
        this.loading = false;
      }
    },
    async refresh() {
      this.$refs.table.buildList();
    },
    async performDeleteFermenter() {
      this.loading = true;
      const results = await this.$apiCall(`mutation EquipmentFermenters($id: ID!) {
                                             objectDelete(id:$id)
                                            }`,
                                          {id:this.fermenter.id});
      this.loading = false;
      if (!results.error) {
        this.showDelete = false;
        this.$store.commit('setError', {type:'success',message:`Deleted fermenter ${this.fermenter.name}`});
        this.refresh();
      }
    },
     async sync() {
      this.loading = true;
      const results = await this.$apiCall(`mutation EquipmentFermenters {
                                             accountingIntegrationSync(step:"fermenters") {name}
                                           }`, {});
      await this.refresh();
      if (!results.error)
        this.$store.commit('setError', {type:'success',message:`Synced fermenters with QBO`});
    }
 }
}
</script>